// @ts-strict-ignore
import moment from 'moment';
import { numberWithCommas } from '~/helpers/formatting';
import type { FilterValuesByColumn } from '../journey-analytics/dashboard/filters/types';
import type { IntervalType } from '../types';
import type { OpenTotalResponseType } from '../investigation-analytics/types';

export const displayError = ({ e, createMessage }) => {
  let message;
  if (e.responseError && e.responseError.message) {
    message = e.responseError.message;
  } else {
    message = 'Something went wrong! Please try again or contact support.';
  }

  createMessage(message, 'error');
};

/**
 * Sets the date to UTC midnight of its local calendar day
 */
export const setDateToUTCMidnight = (date: Date): Date => {
  date.setTime(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return date;
};

export const getUtcMidnightForTimezoneDay = (timestamp: number, timezone: string) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date(timestamp));
  const year = Number(parts.find(({ type }) => type === 'year')?.value);
  const month = Number(parts.find(({ type }) => type === 'month')?.value);
  const day = Number(parts.find(({ type }) => type === 'day')?.value);

  return Date.UTC(year, month - 1, day);
};

export const addZeroDaysToTimeSeries = ({
  formattedSeries,
  minDate,
  maxDate = setDateToUTCMidnight(new Date()).getTime(),
}: {
  formattedSeries: any[];
  minDate?: number;
  maxDate?: number;
}) => {
  const allSeriesWithZeroes = [];

  const MILLISECONDS_IN_DAY = 86400000;
  const DATE_INDEX = 0;
  const VALUE_INDEX = 1;

  for (let i = 0; i < formattedSeries.length; i += 1) {
    const seriesData = [...formattedSeries[i].data];

    if (seriesData?.length) {
      if (minDate && seriesData[0][DATE_INDEX] > minDate) {
        seriesData.unshift([minDate, 0]);
      } else if (!minDate && seriesData[0][VALUE_INDEX] !== 0) {
        const date = seriesData[0][DATE_INDEX];
        seriesData.unshift([date - MILLISECONDS_IN_DAY, 0]);
      }

      if (seriesData[seriesData.length - 1][DATE_INDEX] < maxDate) {
        seriesData.push([maxDate, 0]);
      }
    }

    const seriesDataWithZeroes = seriesData.reduce((dataWithZeroes, datum, index) => {
      const [date, count] = datum;
      if (count === 0) {
        dataWithZeroes.push(datum);
        return dataWithZeroes;
      }

      // Add day with 0 value 1 day before current date if no data exists
      if (
        seriesData[index - 1]
        && seriesData[index - 1][DATE_INDEX] !== date - MILLISECONDS_IN_DAY
      ) {
        dataWithZeroes.push([date - MILLISECONDS_IN_DAY, 0]);
      }

      dataWithZeroes.push(datum);

      // Add day with 0 value 1 day after current date if no data exists
      if (
        seriesData[index + 1]
        && seriesData[index + 1][DATE_INDEX] !== date + MILLISECONDS_IN_DAY
      ) {
        dataWithZeroes.push([date + MILLISECONDS_IN_DAY, 0]);
      }

      return dataWithZeroes;
    }, []);

    allSeriesWithZeroes.push({
      ...formattedSeries[i],
      data: seriesDataWithZeroes,
    });
  }

  return allSeriesWithZeroes;
};

export const addZeroValuesToTimeSeries = ({
  formattedSeries,
  minDate,
  intervalType = 'day',
}: {
  formattedSeries: any[];
  minDate?: number;
  intervalType?: IntervalType;
}) => {
  const allSeriesWithZeroes = [];

  const MILLISECONDS_IN_MINUTE = 60000;
  const MILLISECONDS_IN_HOUR = 3600000;
  const MILLISECONDS_IN_DAY = 86400000;
  const DATE_INDEX = 0;
  const VALUE_INDEX = 1;

  let intervalMilliseconds: number;
  switch (intervalType) {
    case 'minute':
      intervalMilliseconds = MILLISECONDS_IN_MINUTE;
      break;
    case 'hour':
      intervalMilliseconds = MILLISECONDS_IN_HOUR;
      break;
    case 'day':
      intervalMilliseconds = MILLISECONDS_IN_DAY;
      break;
    default:
      throw new Error('Invalid interval type');
  }

  for (let i = 0; i < formattedSeries.length; i += 1) {
    const seriesData = [...formattedSeries[i].data];

    if (seriesData?.length) {
      if (minDate && seriesData[0][DATE_INDEX] > minDate) {
        seriesData.unshift([minDate, 0]);
      } else if (!minDate && seriesData[0][VALUE_INDEX] !== 0) {
        const date = seriesData[0][DATE_INDEX];
        seriesData.unshift([date - intervalMilliseconds, 0]);
      }
    }

    const seriesDataWithZeroes = seriesData.reduce((dataWithZeroes, datum, index) => {
      const [date, count] = datum;
      if (count === 0) {
        dataWithZeroes.push(datum);
        return dataWithZeroes;
      }

      // Add interval with 0 value before current date if no data exists
      if (index === 0 || seriesData[index - 1][DATE_INDEX] !== date - intervalMilliseconds) {
        dataWithZeroes.push([date - intervalMilliseconds, 0]);
      }

      dataWithZeroes.push(datum);

      // Add interval with 0 value after current date if no data exists
      if (
        index === seriesData.length - 1
        || seriesData[index + 1][DATE_INDEX] !== date + intervalMilliseconds
      ) {
        dataWithZeroes.push([date + intervalMilliseconds, 0]);
      }

      return dataWithZeroes;
    }, []);

    allSeriesWithZeroes.push({
      ...formattedSeries[i],
      data: seriesDataWithZeroes,
    });
  }

  return allSeriesWithZeroes;
};

export const getRoundedPercent = ({ count, total }) => Math.round((count / total) * 100);

export const capitalize = (string: String) => string
  .toLowerCase()
  .split(' ')
  .map(s => s.charAt(0).toUpperCase() + s.substring(1))
  .join(' ');

export const convertStateVarsToQueryParams = ({
  filterValuesByColumn,
}: { filterValuesByColumn: FilterValuesByColumn }) => `?filter=${JSON.stringify(filterValuesByColumn)}`;

// Format thousands with comma, and M for million, etc.
export const getFormattedLargeNumber = (num: number) => {
  let formattedNumber = numberWithCommas(num);
  if (num.toString().length > 6) {
    const formatter = Intl.NumberFormat('en', { notation: 'compact' });
    formattedNumber = formatter.format(num);
  }
  return formattedNumber;
};

/**
 * Calc time interval based on createdAtFrom and createdAtTo
 * parse the dates and return 'hour' for diff of 1 day or less,
 * and 'minute' for diff of 1 hour or less, otherwise default to 'day'
 *
 * TODO: since this helper function exists in Typhon as well,
 * need to move this to a shared jslib
 */
export const getTruncTimeInterval = ({
  createdAtFrom,
  createdAtTo,
}: {
  createdAtFrom?: string;
  createdAtTo?: string;
}) => {
  const parsedFrom = createdAtFrom ? moment(createdAtFrom) : moment();
  const parsedTo = createdAtTo ? moment(createdAtTo) : moment();
  const diffHours = parsedTo.diff(parsedFrom, 'hours', true);
  let timeInterval: IntervalType = 'day';

  if (createdAtFrom && createdAtTo) {
    if (diffHours <= 1) {
      timeInterval = 'minute';
    } else if (diffHours <= 24) {
      timeInterval = 'hour';
    }
  }

  return timeInterval;
};

export const formatTimeSeriesData = (data: [string, number][], negate?: boolean): number[][] => {
  const formatted = data
    .map(datum => {
      let value: number | null = datum[1];

      if (value !== null) {
        value = negate ? -Math.abs(datum[1]) : datum[1];
      }

      return [new Date(datum[0]).getTime(), value];
    })
    .sort((a, b) => a[0] - b[0]);

  return formatted;
};

export const timeSeriesNoZeroValues = (data: number[][]): number[][] => {
  const filtered = data.filter(([, value]) => value !== 0);

  return filtered;
};

export const formatAndFillTimeSeriesData = (
  responseData: OpenTotalResponseType,
  createdAtFrom?: string,
  createdAtTo?: string,
): number[][] => {
  const fromDate = createdAtFrom && new Date(createdAtFrom).getTime();
  const toDate = createdAtTo && new Date(createdAtTo).getTime();

  // since the data returned is grouped by start of each day (12AM)
  // use the start of day for the last plotted date
  const toDateMidnight = toDate && moment.utc(toDate).local().startOf('day').toDate()
    .getTime();

  const formattedData = responseData.data
    .map(datum => [new Date(datum.date).getTime(), Number(datum.count)]);

  if (fromDate < formattedData[0][0]) {
    formattedData.unshift([fromDate, 0]);
  }

  if (toDateMidnight > formattedData[formattedData.length - 1][0]) {
    formattedData.push([toDateMidnight, formattedData[formattedData.length - 1][1]]);
  }

  return formattedData;
};
